import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "absuk-lang";
const CACHE_KEY = "absuk-translations-v2";

// Keep in sync with backend translation.py LANGUAGES
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी (Hindi)" },
  { code: "mr", label: "मराठी (Marathi)" },
  { code: "ta", label: "தமிழ் (Tamil)" },
  { code: "te", label: "తెలుగు (Telugu)" },
  { code: "kn", label: "ಕನ್ನಡ (Kannada)" },
  { code: "ml", label: "മലയാളം (Malayalam)" },
  { code: "bn", label: "বাংলা (Bengali)" },
  { code: "gu", label: "ગુજરાતી (Gujarati)" },
  { code: "pa", label: "ਪੰਜਾਬੀ (Punjabi)" },
  { code: "ur", label: "اردو (Urdu)" },
];

const RTL_LANGS = new Set(["ur"]);

const TranslationContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (s) => s,
  translating: false,
});

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE",
  "TEXTAREA", "INPUT", "SELECT", "OPTION", "IFRAME",
  "SVG", "PATH", "CIRCLE", "RECT", "G", "POLYGON", "LINE",
]);

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota — ignore */
  }
}

function isTranslatable(text) {
  if (!text) return false;
  const trimmed = text.trim();
  if (trimmed.length < 2) return false;
  // Need at least one alphabetic letter (latin/extended) so we don't translate "—" "·" "•" "£24"
  if (!/[A-Za-zÀ-ÿ]/.test(trimmed)) return false;
  return true;
}

function shouldSkipNode(node) {
  let p = node.parentElement;
  while (p) {
    if (SKIP_TAGS.has(p.tagName)) return true;
    if (p.hasAttribute && p.hasAttribute("data-no-translate")) return true;
    if (p.isContentEditable) return true;
    p = p.parentElement;
  }
  return false;
}

// WeakMap<TextNode, { original: string, currentLang: string }>
function collectTextNodes(root, originals) {
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      // Use the original (pre-translation) text if we have one cached
      const meta = originals.get(node);
      const value = meta ? meta.original : node.nodeValue;
      if (!isTranslatable(value)) return NodeFilter.FILTER_REJECT;
      if (shouldSkipNode(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) out.push(n);
  return out;
}

export function TranslationProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "en";
  });
  const [translating, setTranslating] = useState(false);

  const cacheRef = useRef(loadCache()); // { lang: { sourceText: translation } }
  const originalsRef = useRef(new WeakMap()); // node -> {original, currentLang}
  const inflightRef = useRef(false);
  const debounceRef = useRef(null);
  const observerRef = useRef(null);

  // Apply <html lang> + dir
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
  }, [lang]);

  const setLang = useCallback((newLang) => {
    localStorage.setItem(STORAGE_KEY, newLang);
    setLangState(newLang);
  }, []);

  // Translate / restore the entire page for the given language
  const runTranslation = useCallback(
    async (targetLang) => {
      if (inflightRef.current) return;
      inflightRef.current = true;

      const root = document.body;
      if (!root) {
        inflightRef.current = false;
        return;
      }

      const originals = originalsRef.current;
      const nodes = collectTextNodes(root, originals);

      // Pause the observer while we mutate
      const obs = observerRef.current;
      if (obs) obs.disconnect();

      try {
        if (targetLang === "en") {
          // Restore originals
          for (const node of nodes) {
            const meta = originals.get(node);
            if (meta && node.nodeValue !== meta.original) {
              node.nodeValue = meta.original;
            }
            if (meta) meta.currentLang = "en";
          }
          return;
        }

        // Capture originals + current values
        const cache = cacheRef.current[targetLang] || {};
        cacheRef.current[targetLang] = cache;

        const todo = []; // [{node, source}]
        const toFetch = new Set();

        for (const node of nodes) {
          let meta = originals.get(node);
          if (!meta) {
            meta = { original: node.nodeValue, currentLang: "en" };
            originals.set(node, meta);
          }
          if (meta.currentLang === targetLang) continue; // already translated
          const source = meta.original;
          todo.push({ node, source });
          if (!cache[source]) toFetch.add(source.trim());
        }

        // Fetch missing translations in chunks of 80 (well under backend cap of 200)
        const missing = Array.from(toFetch);
        for (let i = 0; i < missing.length; i += 80) {
          const chunk = missing.slice(i, i + 80);
          setTranslating(true);
          try {
            const res = await axios.post(
              `${API_BASE}/api/i18n/translate`,
              { lang: targetLang, strings: chunk },
              { withCredentials: false, timeout: 30000 }
            );
            const translations = res.data?.translations || [];
            chunk.forEach((src, idx) => {
              cache[src] = translations[idx] || src;
            });
          } catch (err) {
            // On failure, mark these as their originals so we don't retry forever this session
            chunk.forEach((src) => {
              if (!cache[src]) cache[src] = src;
            });
          }
        }
        saveCache(cacheRef.current);

        // Apply translations to nodes (preserve leading/trailing whitespace)
        for (const { node, source } of todo) {
          const trimmed = source.trim();
          const leading = source.slice(0, source.indexOf(trimmed[0] || ""));
          const trailing = source.slice(source.lastIndexOf(trimmed[trimmed.length - 1] || "") + 1);
          const translation = cache[trimmed] || trimmed;
          const finalText = `${leading}${translation}${trailing}`;
          if (node.nodeValue !== finalText) node.nodeValue = finalText;
          const meta = originals.get(node);
          if (meta) meta.currentLang = targetLang;
        }
      } finally {
        setTranslating(false);
        inflightRef.current = false;
        // Re-attach observer
        if (observerRef.current) {
          observerRef.current.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }
    },
    []
  );

  const scheduleTranslate = useCallback(
    (targetLang) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        runTranslation(targetLang);
      }, 120);
    },
    [runTranslation]
  );

  // Watch for DOM changes (route changes, async data) and re-translate
  useEffect(() => {
    const obs = new MutationObserver((mutations) => {
      // Skip if all mutations are inside elements we'd ignore anyway
      let interesting = false;
      for (const m of mutations) {
        if (m.type === "childList" && (m.addedNodes.length || m.removedNodes.length)) {
          interesting = true;
          break;
        }
        if (m.type === "characterData") {
          // characterData mutation: only interesting if the new value isn't one we just set
          const node = m.target;
          const meta = originalsRef.current.get(node);
          if (!meta || meta.currentLang !== lang) {
            interesting = true;
            break;
          }
        }
      }
      if (interesting) scheduleTranslate(lang);
    });
    observerRef.current = obs;
    if (lang !== "en" || originalsRef.current) {
      obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    return () => obs.disconnect();
  }, [lang, scheduleTranslate]);

  // Run translation whenever lang changes
  useEffect(() => {
    runTranslation(lang);
  }, [lang, runTranslation]);

  // Per-string translator (for <T> wrappers — small, synchronous best-effort lookup).
  // For brand-new strings the auto-translator will pick them up shortly.
  const t = useCallback(
    (text) => {
      if (!text || typeof text !== "string") return text;
      if (lang === "en") return text;
      const cache = cacheRef.current[lang] || {};
      return cache[text.trim()] || text;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, translating }),
    [lang, setLang, t, translating]
  );
  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

export function useTranslation() {
  return useContext(TranslationContext);
}

export function T({ children }) {
  const { t } = useTranslation();
  if (typeof children !== "string") return <>{children}</>;
  return <>{t(children)}</>;
}
